import { toast } from 'react-toastify';
import { Dialog } from '@headlessui/react';
import { useForm } from 'react-hook-form';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { Player } from '../../types/players';
import MyDrawer from '../ui/Drawer';
import { DATE_FORMATS, getDateForCalendar } from '../../utils/dates';
import { HOURS_MINUTES_REGEX } from '../../utils/regex';
import { COMMON_TRANSLATE_KEYS } from '../../constants/translate-keys';

import TeamLogo from '../../icons/images/team.png';
import NoTeamLogo from '../../icons/images/no-team.png';
import LocationIcon from '../../icons/svg/location.svg';
import ClockIcon from '../../icons/svg/clock.svg';
import Button from '../buttons/Button';
import {
  acceptReservation,
  declineReservation,
  declineSingleGame,
} from '../../api/reservations';
import InputField from '../forms/form-elements/InputField';
import ReservationPlayersModal from './ReservationPlayersModal';
import { useResponsiveWindow } from '../../hooks/useResponsiveWindow';
import { useAuth } from '../../hooks/useAuth';
import EventStatusBadge from '../ui/EventStatusBadge';
import CustomRadioGroup from '../forms/form-elements/CustomRadioGroup';
import { Game } from '../../types';
import { getObjectTranslation } from '../../utils/language';

type TProps = {
  isOpen: boolean;
  game: Game | null;
  onPlayerClick: (player: Player) => void;
  closeDrawer: () => void;
  fetchReservations: () => Promise<void>;
};

interface IFormInput {
  reason: string;
}

const GameReservationDetailsModal = ({
  isOpen,
  game,
  closeDrawer,
  onPlayerClick,
  fetchReservations,
}: TProps) => {
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [openPlayersModal, setOpenPlayersModal] = useState(false);
  const [selectedDeclineReason, setSelectedDeclineReason] = useState<
    string | null
  >(null);
  const { isMobile } = useResponsiveWindow();

  const user = useAuth();

  const { t, i18n } = useTranslation();

  const {
    register,
    setValue,
    setFocus,
    getValues,
    formState: { isValid },
  } = useForm<IFormInput>({
    defaultValues: {
      reason: '',
    },
  });

  const getPlayTime = (start: string, end: string) => {
    if (start.match(HOURS_MINUTES_REGEX) && end.match(HOURS_MINUTES_REGEX)) {
      return `${start} - ${end}`;
    }

    return `${dayjs(getDateForCalendar(start)).format(
      DATE_FORMATS.HOURS_MINUES
    )} - ${dayjs(getDateForCalendar(end)).format(DATE_FORMATS.HOURS_MINUES)}`;
  };

  const CancelSuggestions = useMemo(() => {
    return [
      {
        id: 1,
        name: t(COMMON_TRANSLATE_KEYS.CANCEL_RESERVATION_REASON_1),
      },
      {
        id: 2,
        name: t(COMMON_TRANSLATE_KEYS.CANCEL_RESERVATION_REASON_2),
      },
      {
        id: 3,
        name: t(COMMON_TRANSLATE_KEYS.CANCEL_RESERVATION_REASON_3),
      },
      {
        id: 4,
        name: t(COMMON_TRANSLATE_KEYS.CANCEL_RESERVATION_REASON_4),
      },
      {
        id: 5,
        name: t(COMMON_TRANSLATE_KEYS.CANCEL_RESERVATION_REASON_5),
      },
    ];
  }, [t]);

  const handleAcceptReservation = useCallback(async () => {
    if (!game) {
      return;
    }

    const { data, error } = await acceptReservation(game.reservation.id);

    if (error) {
      const { error: errMsg } = error;

      toast(errMsg, {
        toastId: 'accept-res-error',
        theme: 'dark',
        bodyClassName: 'text-center',
      });

      return;
    }

    if (data) {
      await fetchReservations();
    }

    closeDrawer();
  }, [closeDrawer, fetchReservations, game]);

  const closeModal = useCallback(() => {
    setSelectedDeclineReason(null);
    setOpenCancelModal(false);
    setValue('reason', '', {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    });
  }, [setValue]);

  const handleCancelReservation = useCallback(async () => {
    if (!game) {
      closeModal();
      return;
    }

    const payload = {
      reason: getValues('reason'),
    };

    const { data, error } = await declineReservation(
      game.reservation.id,
      payload
    );

    if (error) {
      const { error: errMsg } = error;

      toast(errMsg, {
        toastId: 'decline-res-error',
        theme: 'dark',
        bodyClassName: 'text-center',
      });

      return;
    }

    if (data) {
      await fetchReservations();
    }

    closeModal();
    closeDrawer();
  }, [fetchReservations, game, getValues, closeDrawer, closeModal]);

  const handleCancelGame = useCallback(async () => {
    if (!game) {
      closeModal();
      return;
    }

    const payload = {
      reason: getValues('reason'),
    };

    const { data, error } = await declineSingleGame(
      game.reservation.id,
      game.id,
      payload
    );

    if (error) {
      const { error: errMsg } = error;

      toast(errMsg, {
        toastId: 'decline-game-error',
        theme: 'dark',
        bodyClassName: 'text-center',
      });

      return;
    }

    if (data) {
      await fetchReservations();
    }

    closeModal();
    closeDrawer();
  }, [fetchReservations, game, getValues, closeDrawer, closeModal]);

  const onSelectDeclineReason = useCallback(
    (value: string | ((prevState: string | null) => string | null) | null) => {
      setSelectedDeclineReason(value);

      if (value) {
        setValue('reason', value as string, { shouldValidate: true });
      }
    },
    [setValue]
  );

  const renderDateInfo = useCallback(() => {
    if (!game) {
      return <></>;
    }

    const { date, start, end } = game;

    return (
      <>
        <div className="flex flex-row items-start gap-2">
          <img className="mt-1" src={ClockIcon} alt="Clock" />
          <div>
            <p>
              {`${t(COMMON_TRANSLATE_KEYS.DATE)}: ${
                date
                  ? dayjs(date)
                      .locale(i18n.language)
                      .format(DATE_FORMATS.DATE_DOTS)
                  : t(COMMON_TRANSLATE_KEYS.RECURRING)
              }`}
            </p>
            <p>
              {`${t(COMMON_TRANSLATE_KEYS.DAY)}: ${
                date
                  ? dayjs(date)
                      .locale(i18n.language)
                      .format(DATE_FORMATS.WEEKDAY_ONLY)
                  : t(COMMON_TRANSLATE_KEYS.RECURRING)
              } `}
            </p>
            <p>{`${t(COMMON_TRANSLATE_KEYS.TIME)}: ${getPlayTime(
              start,
              end
            )}`}</p>
          </div>
        </div>
        {/* {isRecurring && (
          <div className="flex flex-row items-center gap-2">
            <CalendarDaysIcon width={20} />
            <p>
              {t(COMMON_TRANSLATE_KEYS.PERIOD)}:{' '}
              {`${dayjs(recurringStartDate)
                .locale(i18n.language)
                .format(DATE_FORMATS.DATE_DOTS)} - ${dayjs(recurringEndDate)
                .locale(i18n.language)
                .format(DATE_FORMATS.DATE_DOTS)}`}
            </p>
          </div>
        )} */}
      </>
    );

    // return (
    //   <div className="flex flex-col">
    //     {isRecurring && recurringDays && (
    //       <p className="whitespace-break-spaces">
    //         <span className="text-mainBlue">
    //           {t(COMMON_TRANSLATE_KEYS.RECURRING_DAYS)}:{' '}
    //         </span>
    //         {getRecurringDays(recurringDays).join(', ')}
    //       </p>
    //     )}
    //     {isRecurring && recurringEndDate && (
    //       <p>
    //         <span className="text-mainBlue">
    //           {t(COMMON_TRANSLATE_KEYS.END_RECURRING_DAY)}:{' '}
    //         </span>
    //         {dayjs(recurringEndDate)
    //           .locale(i18n.language)
    //           .format('dddd, DD.MM.YYYY')}
    //       </p>
    //     )}
    //     {isRecurring && recurringRepeatCount && (
    //       <p>
    //         <span className="text-mainBlue">
    //           {t(COMMON_TRANSLATE_KEYS.REPEAT)}:{' '}
    //         </span>
    //         {t(COMMON_TRANSLATE_KEYS.WEEK_COUNT, {
    //           count: recurringRepeatCount,
    //         })}
    //       </p>
    //     )}
    //   </div>
    // );
  }, [i18n.language, game, t]);

  const renderPriceInfo = useCallback(() => {
    if (!game) {
      return <></>;
    }

    const { price, currencyData } = game;

    if (!price || !currencyData) {
      return <></>;
    }

    return (
      <p className="flex flex-row items-start gap-2 whitespace-break-spaces">
        <span className="text-mainBlue">{`${t(
          COMMON_TRANSLATE_KEYS.PRICE
        )}:`}</span>{' '}
        <span>{`${price.toFixed(2)} ${getObjectTranslation(
          currencyData,
          i18n.language
        )}`}</span>
      </p>
    );
  }, [game, t, i18n.language]);

  const renderButtons = useCallback(() => {
    if (!game) {
      return <></>;
    }

    const { status, isExpired } = game;

    if (status === 'CONFIRMED' && !isExpired) {
      return (
        <div className="flex flex-col gap-1">
          <Button
            className="h-full"
            textColor="text-red-500"
            borderColor="border-red-500"
            border="border"
            onClick={() => setOpenCancelModal(true)}
          >
            {t(COMMON_TRANSLATE_KEYS.CANCEL)}
          </Button>
        </div>
      );
    }

    if (status === 'PENDING' && !isExpired) {
      return (
        <div className="flex flex-col gap-1">
          <div className="flex flex-row justify-around gap-x-2">
            <Button
              backgroundColor="bg-mainGreen"
              textColor="text-mainBlack"
              width={isMobile ? undefined : 30}
              onClick={async () => await handleAcceptReservation()}
            >
              {t(COMMON_TRANSLATE_KEYS.ACCEPT)}
            </Button>
            <Button
              backgroundColor="bg-functionalRed"
              textColor="text-mainWhite"
              width={isMobile ? undefined : 30}
              onClick={() => setOpenCancelModal(true)}
            >
              {t(COMMON_TRANSLATE_KEYS.CANCEL)}
            </Button>
          </div>
        </div>
      );
    }

    return <></>;
  }, [game, handleAcceptReservation, isMobile, t]);

  const renderModalContent = useCallback(() => {
    if (!game) {
      return <></>;
    }

    const { field, reservation, isPublic, status, isExpired } = game;

    const {
      team,
      creator,
      otherName,
      otherPhone,
      otherUser,
      createdAt,
      canceledAt,
      updatedAt,
      isRecurring,
    } = reservation;

    if (team) {
      return (
        <div className="h-full overflow-y-auto">
          <div className="flex justify-between">
            <EventStatusBadge
              status={status}
              isExpired={isExpired}
              className="m-0 ml-0 mb-4"
              noXMargin
            />
            <button onClick={closeDrawer} className="text-white">
              X
            </button>
          </div>
          <div className="bg-additionalBlack rounded-lg h-full">
            <div className="flex">
              <img
                src={team.logoUrl || TeamLogo}
                alt="Team"
                className="object-cover w-full h-[240px] rounded-lg"
              />
            </div>
            <div className="flex flex-col p-3 gap-5">
              <div className="flex flex-col">
                <h2 className="font-inter text-2xl not-italic font-bold leading-7">
                  {isPublic
                    ? t(COMMON_TRANSLATE_KEYS.PUBLIC_GAME)
                    : t(COMMON_TRANSLATE_KEYS.PRIVATE_GAME)}{' '}
                </h2>
                <p className="font-normal text-base text-midGray">{`${
                  isRecurring
                    ? t(COMMON_TRANSLATE_KEYS.RECURRING_RESERVATION)
                    : t(COMMON_TRANSLATE_KEYS.SINGLE_RESERVATION)
                }`}</p>
              </div>
              <div className="flex flex-col gap-5">
                <div className="flex flex-col gap-1">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      setOpenPlayersModal(true);
                    }}
                    className="font-inter text-2xl not-italic font-bold leading-7 border-0 bg-transparent text-start hover:underline"
                  >
                    {`${t(COMMON_TRANSLATE_KEYS.TEAM).toLowerCase()} ${
                      team.name
                    }`}
                  </button>
                  <div className="flex flex-col gap-1">
                    <p className="text-mainBlue">
                      {t(COMMON_TRANSLATE_KEYS.CAPTAIN)}:{' '}
                      <span
                        className="text-white hover:underline hover:cursor-pointer"
                        onClick={() => onPlayerClick(team.captain)}
                      >
                        {team.captain.name}
                      </span>
                    </p>
                    <p className="text-mainBlue">
                      {t(COMMON_TRANSLATE_KEYS.CONTACT_CREATOR)}:{' '}
                      <span className="text-white">{team.captain.phone}</span>
                    </p>
                  </div>
                  {/* {team.secondCaptain && (
                    <div className="flex flex-col gap-1">
                      <p className="text-mainBlue">
                        {t(COMMON_TRANSLATE_KEYS.SECOND_CAPTAIN)}:{' '}
                        <span
                          className="text-white hover:underline hover:cursor-pointer"
                          onClick={() => onPlayerClick(team.secondCaptain!)}
                        >
                          {team.secondCaptain.name}
                        </span>
                      </p>
                      <p className="text-mainBlue">
                        {t(COMMON_TRANSLATE_KEYS.CONTACT_CREATOR)}:{' '}
                        <span className="text-white">
                          {team.secondCaptain.phone}
                        </span>
                      </p>
                    </div>
                  )} */}
                </div>

                <div className="flex flex-col gap-1">
                  <div className="flex flex-row">
                    <p className="text-mainBlue">
                      {`${t(COMMON_TRANSLATE_KEYS.COMPLEX)}:`}{' '}
                      <span className="text-white">{field.complexName}</span>
                    </p>
                  </div>
                  <div className="flex flex-row">
                    <p className="text-mainBlue">
                      {`${t(COMMON_TRANSLATE_KEYS.FIELD)}:`}{' '}
                      <span className="text-white">{field.name}</span>
                    </p>
                  </div>
                  {renderPriceInfo()}
                  {/* <div>
                    <div className="flex flex-row items-start gap-1">
                      <span className="flex gap-2">
                        <img src={LocationIcon} alt="Location" />
                        {`${t(COMMON_TRANSLATE_KEYS.ADDRESS)}:`}
                      </span>
                      <span>
                        <p>{`${[field.city, field.district]
                          .filter((x) => x)
                          .join(', ')},`}</p>
                      </span>
                    </div>
                    <p className="text-midGray ml-6">{`${field.address}`}</p>
                  </div> */}
                  {/* {otherName && (
                    <div className="flex flex-col gap-2">
                      <p className="text-mainBlue">
                        {t(COMMON_TRANSLATE_KEYS.RESERVED_FOR)}:{' '}
                        <span className="text-white">{otherName}</span>
                      </p>
                      <p className="text-mainBlue">
                        {t(COMMON_TRANSLATE_KEYS.CONTACT_CREATOR)}:{' '}
                        <span className="text-white">{otherPhone}</span>
                      </p>
                    </div>
                  )} */}
                  {renderDateInfo()}
                </div>

                {renderButtons()}

                <div className="flex flex-col gap-1">
                  {['DECLINED'].includes(status) && (
                    <div className="flex flex-row">
                      <p className="text-functionalRed">
                        {t(COMMON_TRANSLATE_KEYS.CANCELLED_AT)}:{' '}
                        <span className="text-white">
                          {dayjs(canceledAt || updatedAt).format(
                            'DD.MM.YYYY HH:mm'
                          )}
                        </span>
                      </p>
                    </div>
                  )}
                  {['DECLINED'].includes(status) && game.declineReason && (
                    <div className="flex flex-row h-full">
                      <p className="text-functionalRed">
                        {t(COMMON_TRANSLATE_KEYS.DECLINE_REASON)}
                        {': '}
                        <span className="text-white">{game.declineReason}</span>
                      </p>
                    </div>
                  )}
                  {(creator.id !== team.captain.id ||
                    creator.id !== team.secondCaptain?.id) && (
                    <>
                      <p className="text-mainBlue">
                        {t(COMMON_TRANSLATE_KEYS.RESERVED_BY)}:{' '}
                        {creator.id !== user.user?.id && (
                          <span
                            className="text-white hover:underline hover:cursor-pointer"
                            onClick={() => onPlayerClick(creator)}
                          >
                            {creator.name}
                          </span>
                        )}
                        {creator.id === user.user?.id && (
                          <span className="text-white">
                            {`${creator.name} ${
                              creator.id === user.user?.id
                                ? `(${t(COMMON_TRANSLATE_KEYS.YOU)})`
                                : ''
                            }`}
                          </span>
                        )}
                      </p>
                      {/* <p className="text-mainBlue">
                        {t(COMMON_TRANSLATE_KEYS.CONTACT_CREATOR)}:{' '}
                        <span className="text-white">{creator.phone}</span>
                      </p> */}
                    </>
                  )}
                  <div className="flex flex-row">
                    <p className="text-mainBlue">
                      {t(COMMON_TRANSLATE_KEYS.CREATED_AT)}:{' '}
                      <span className="text-white">
                        {dayjs(createdAt).format('DD.MM.YYYY HH:mm')}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="h-full overflow-y-auto">
        <div className="flex justify-between">
          <EventStatusBadge
            status={status}
            isExpired={isExpired}
            className="m-0 ml-0 mb-4"
            noXMargin
          />
          <button onClick={closeDrawer} className="text-white">
            X
          </button>
        </div>
        <div className="bg-additionalBlack rounded-lg">
          <div className="flex">
            <img
              src={NoTeamLogo}
              alt="Team"
              className="object-cover w-full h-[240px]"
            />
          </div>
          <div className="flex flex-col p-3 gap-5">
            <div className="flex flex-col">
              <h2 className="font-inter text-2xl not-italic font-bold leading-7">
                {isPublic
                  ? t(COMMON_TRANSLATE_KEYS.PUBLIC_GAME)
                  : t(COMMON_TRANSLATE_KEYS.PRIVATE_GAME)}{' '}
              </h2>
              <p className="font-normal text-base text-midGray">{`${
                isRecurring
                  ? t(COMMON_TRANSLATE_KEYS.RECURRING_RESERVATION)
                  : t(COMMON_TRANSLATE_KEYS.SINGLE_RESERVATION)
              }`}</p>
            </div>
            <div className="flex flex-col gap-5">
              <div className="flex flex-col gap-1">
                <div className="flex flex-row">
                  <p className="text-mainBlue">
                    {`${t(COMMON_TRANSLATE_KEYS.COMPLEX)}:`}{' '}
                    <span className="text-white">{field.complexName}</span>
                  </p>
                </div>
                <div className="flex flex-row">
                  <p className="text-mainBlue">
                    {`${t(COMMON_TRANSLATE_KEYS.FIELD)}:`}{' '}
                    <span className="text-white">{field.name}</span>
                  </p>
                </div>
                {renderPriceInfo()}
                {/* <div>
                  <div className="flex flex-row items-start gap-1">
                    <span className="flex gap-2">
                      <img src={LocationIcon} alt="Location" />
                      {`${t(COMMON_TRANSLATE_KEYS.ADDRESS)}:`}
                    </span>
                    <span>
                      <p>{`${[field.city, field.district]
                        .filter((x) => x)
                        .join(', ')},`}</p>
                    </span>
                  </div>
                  <p className="text-midGray ml-6">{`${field.address}`}</p>
                </div> */}
                {renderDateInfo()}
              </div>

              {renderButtons()}

              <div className="flex flex-col gap-1">
                {['DECLINED'].includes(status) && (
                  <div className="flex flex-row">
                    <p className="text-functionalRed">
                      {t(COMMON_TRANSLATE_KEYS.CANCELLED_AT)}:{' '}
                      <span className="text-white">
                        {dayjs(canceledAt || updatedAt).format(
                          'DD.MM.YYYY HH:mm'
                        )}
                      </span>
                    </p>
                  </div>
                )}
                {['DECLINED'].includes(status) && game.declineReason && (
                  <div className="flex flex-col h-full">
                    <p className="text-functionalRed">
                      {t(COMMON_TRANSLATE_KEYS.DECLINE_REASON)}
                      {': '}
                      <span className="text-white">{game.declineReason}</span>
                    </p>
                  </div>
                )}
                {otherName && (
                  <div className="flex flex-col gap-1">
                    {!!otherUser ? (
                      <p className="text-mainBlue">
                        {t(COMMON_TRANSLATE_KEYS.RESERVED_FOR)}:{' '}
                        <span
                          className="text-white hover:underline hover:cursor-pointer"
                          onClick={(e) => {
                            e.preventDefault();
                            console.log('click', otherUser);
                            onPlayerClick(otherUser)
                          }}
                        >
                          {otherUser.name}
                        </span>
                      </p>
                    ) : (
                      <p className="text-mainBlue">
                        {t(COMMON_TRANSLATE_KEYS.RESERVED_FOR)}:{' '}
                        <span className="text-white">{otherName}</span>
                      </p>
                    )}
                    <p className="text-mainBlue">
                      {t(COMMON_TRANSLATE_KEYS.CONTACT_CREATOR)}:{' '}
                      <span className="text-white">{otherPhone}</span>
                    </p>
                  </div>
                )}
                <div className="flex flex-col gap-1">
                  <p className="text-mainBlue">
                    {t(COMMON_TRANSLATE_KEYS.RESERVED_BY)}:{' '}
                    <span className="text-white">
                      {`${creator.name} ${
                        creator.id === user.user?.id
                          ? `(${t(COMMON_TRANSLATE_KEYS.YOU)})`
                          : ''
                      }`}
                    </span>
                  </p>
                  {creator.id !== user.user?.id && (
                    <p className="text-mainBlue">
                      {t(COMMON_TRANSLATE_KEYS.CONTACT_CREATOR)}:{' '}
                      <span className="text-white">{creator.phone}</span>
                    </p>
                  )}
                </div>
                <div className="flex flex-row">
                  <p className="text-mainBlue">
                    {t(COMMON_TRANSLATE_KEYS.CREATED_AT)}:{' '}
                    <span className="text-white">
                      {dayjs(createdAt).format('DD.MM.YYYY HH:mm')}
                    </span>
                  </p>
                </div>
              </div>
            </div>

            {/* <div className="flex flex-row items-start gap-2">
              <img src={PlayerIcon} alt="Players" />
              <p>{`${t(COMMON_TRANSLATE_KEYS.PLAYERS, {
                count: Number(field.playerSize.size),
              })}: ${field.playerSize.size}`}</p>
            </div> */}
          </div>
        </div>
      </div>
    );
  }, [
    game,
    closeDrawer,
    t,
    user.user?.id,
    renderDateInfo,
    renderPriceInfo,
    onPlayerClick,
    renderButtons,
  ]);

  useEffect(() => {
    if (openCancelModal) {
      setFocus('reason', { shouldSelect: true });
    }
  }, [openCancelModal, setFocus]);

  return (
    <>
      <MyDrawer isOpen={isOpen}>{renderModalContent()}</MyDrawer>

      <ReservationPlayersModal
        isOpen={openPlayersModal}
        onClose={() => setOpenPlayersModal(false)}
        team={game?.reservation?.team}
        onPlayerClick={onPlayerClick}
      />

      <Dialog
        open={openCancelModal}
        onClose={() => closeModal()}
        className="fixed inset-0 flex items-center justify-center z-[110]"
      >
        <Dialog.Overlay className="fixed inset-0 bg-black  opacity-50" />
        <div className="bg-additionalBlack p-6 rounded-xl absolute top-212 left-468 lg:max-w-[50%] max-w-[100%] max-h-[100%] overflow-auto mx-6">
          <div className="flex flex-row justify-between mb-3 items-start">
            <Dialog.Title className="text-white font-bold text-2xl">
              {t(COMMON_TRANSLATE_KEYS.CANCEL_RESERVATION_MODAL_TITLE)}
            </Dialog.Title>
            <button
              className="ps-3 pb-3 text-white"
              onClick={() => closeModal()}
            >
              X
            </button>
          </div>
          <div className="w-full"></div>
          <div className="w-full">
            <p className="text-white text-sm mt-6 mb-4">
              {t(COMMON_TRANSLATE_KEYS.DECLINE_REASON)}{' '}
              <span className="text-functionalRed">*</span>
            </p>
          </div>
          <div className="mb-6">
            <InputField
              {...register('reason', { required: true })}
              placeholder={t(
                COMMON_TRANSLATE_KEYS.CANCEL_RESERVATION_REASON_PLACEHOLDER
              )}
              height={3}
            />
          </div>
          <div className="flex flex-col max-h-[30vh] overflow-auto -mr-5">
            <CustomRadioGroup
              items={CancelSuggestions}
              value={selectedDeclineReason}
              onChange={onSelectDeclineReason}
              className="flex flex-col gap-3 my-3 mr-5"
            />
          </div>
          {game?.reservation?.isRecurring && (
            <p className="border-y border-dividerGray p-3 my-5 text-textGray">
              {t(COMMON_TRANSLATE_KEYS.CANCEL_RECURRING_RESERVATION_TEXT)}
            </p>
          )}
          <div className="flex flex-row justify-center gap-3 shrink-0">
            {!game?.reservation?.isRecurring && (
              <Button
                textColor="text-white"
                backgroundColor="bg-functionalRed"
                width={16}
                height={3}
                onClick={async () => await handleCancelReservation()}
                isDisabled={!isValid}
              >
                {t(COMMON_TRANSLATE_KEYS.CONFIRM)}
              </Button>
            )}
            {game?.reservation?.isRecurring && (
              <>
                <Button
                  textColor="text-white"
                  backgroundColor="bg-functionalRed"
                  width={16}
                  height={3}
                  onClick={async () => await handleCancelReservation()}
                  isDisabled={!isValid}
                >
                  {t(COMMON_TRANSLATE_KEYS.CONFIRM_ALL)}
                </Button>
                <Button
                  textColor="text-functionalRed"
                  borderColor="border-functionalRed"
                  border="border"
                  width={16}
                  height={3}
                  onClick={async () => await handleCancelGame()}
                  isDisabled={!isValid}
                >
                  {t(COMMON_TRANSLATE_KEYS.CONFIRM_THIS)}
                </Button>
              </>
            )}
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default GameReservationDetailsModal;
